import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, ExternalLink, ArrowDown, FlaskConical,
  AlertTriangle, BookOpen, Microscope,
} from 'lucide-react';
import SubpageLayout from '../components/FlexiOssCinematic/SubpageLayout';
import ReadAloud from '../components/ReadAloud';
import StructuredData from '../components/StructuredData';
import { SITE_URL } from '../lib/siteConfig';
import { useTheme } from '../contexts/ThemeContext';

const FAQ_ITEMS = [
  {
    id: 1,
    q: 'The graft was brittle and hard to mold into an irregular defect.',
    a: "Unmodified ceramic hydroxyapatite is mechanically fragile: it has low fracture resistance, tends to crumble during handling, and can't be reshaped to fit an irregular bone cavity. FlexiOss addresses this structurally rather than chemically — the HAP granules are physically entrapped inside a curdlan triple-helix gel, which is what gives the composite its elasticity. In practice, that means soaking the material in sterile saline for 15 minutes and then hand-molding it directly into the curetted bone bed. Published cases, including a rabbit tibial-defect model and a human case series, report good conformity to irregular defect shapes during surgical handling.",
    limitation: null,
    sources: [{ label: 'Kotrych et al., J Clin Med 2025', href: 'https://www.mdpi.com/2077-0383/14/11/3738' }],
  },
  {
    id: 2,
    q: 'It never seemed to turn into real bone — it just sat there.',
    a: 'Poor resorption is the other classic complaint about ceramic HAP. The remodeling evidence for FlexiOss includes a rabbit tibial-defect model showing bone reorganization and new woven bone at the implant interface within one month; a femoral defect case report showing mineralization progressing to complete bone regeneration between 12 and 19 months; and an 8-patient case series in benign bone tumor surgery showing scintigraphic signs of active remodeling at 12 months, with radiographic scores indicating healed bone in 6 of 8 patients.',
    limitation: 'These are small case series and a single animal model — not yet a large controlled trial. A multi-center randomized trial (FlexiOssOT) directly comparing FlexiOss to standard of care in trauma bone defects is currently enrolling.',
    sources: [
      { label: 'Kotrych et al., J Clin Med 2025', href: 'https://www.mdpi.com/2077-0383/14/11/3738' },
      { label: 'FlexiOssOT — NCT07209033', href: 'https://clinicaltrials.gov/study/NCT07209033' },
    ],
  },
  {
    id: 3,
    q: 'Loose granules migrated out of the defect.',
    a: 'Particle migration is a known issue with loose granular HAP, which has no mechanism holding it in place once implanted. In FlexiOss, the granules are bound within the curdlan gel rather than delivered as free particulate, which should reduce the likelihood of migration into surrounding soft tissue or adjacent joint space.',
    limitation: "No dedicated study has directly compared FlexiOss against loose-granule HAP for migration specifically. This is a reasonable inference from the material's structure, not a separately proven outcome.",
    sources: null,
  },
  {
    id: 4,
    q: 'I was worried about infection.',
    a: "Conventional HAP grafts typically aren't designed to carry or release a drug. FlexiOss has been studied as an antibiotic carrier: composite loaded with aminoglycoside antibiotics (gentamicin or amikacin) released the drug in a biphasic, pore-dependent pattern over roughly 2–5 days, and inhibited growth of both Gram-positive and Gram-negative bacterial strains associated with orthopedic infection in laboratory testing.",
    limitation: 'The study authors describe this release profile as suited to perioperative antibiotic prophylaxis at the time of implantation — not as a treatment for an already-established bone infection.',
    sources: [{ label: 'Belcarz, Zima & Ginalska, Int J Pharm 2013', href: 'https://pubmed.ncbi.nlm.nih.gov/23850627/' }],
  },
  {
    id: 5,
    q: 'There was a local inflammatory or allergic-type reaction.',
    a: 'Hydroxyapatite itself is generally non-immunogenic, but composite formulations can behave differently depending on the binder used. FlexiOss has been tested in human serum at physiological pH, where it showed no uncontrolled changes in weight, volume, porosity, or mechanical properties, and separate laboratory work found no toxicity to osteoblasts.',
    limitation: "True hypersensitivity to calcium-phosphate ceramics is rare but does occur in individual patients. This data doesn't rule that out on a case-by-case basis.",
    sources: [{ label: 'Borkowski et al., J Biomed Mater Res B 2018', href: 'https://onlinelibrary.wiley.com/doi/abs/10.1002/jbm.b.34082' }],
  },
];

const RESEARCH_LIBRARY = [
  { label: 'Full FlexiOss publication bibliography', href: 'https://flexioss.pl/en/publications/' },
  { label: 'Kotrych et al., J Clin Med 2025 — enchondroma case series', href: 'https://www.mdpi.com/2077-0383/14/11/3738' },
  { label: 'Borkowski et al., J Biomed Mater Res B 2018 — behavior in human serum', href: 'https://onlinelibrary.wiley.com/doi/abs/10.1002/jbm.b.34082' },
  { label: 'Borkowski et al., Mater Sci Eng C 2016 — bioactivity comparison', href: 'https://pubmed.ncbi.nlm.nih.gov/26952422/' },
  { label: 'Belcarz, Zima & Ginalska, Int J Pharm 2013 — antibiotic-loaded composite', href: 'https://pubmed.ncbi.nlm.nih.gov/23850627/' },
  { label: 'Misztal-Kunecka et al., Veterinary Sciences 2024 — veterinary applications', href: 'https://www.mdpi.com/2306-7381/11/12/633' },
  { label: 'FlexiOssOT randomized controlled trial (ongoing)', href: 'https://clinicaltrials.gov/study/NCT07209033' },
];

const DISCLAIMER = "Evidence summarized here includes small case series and studies involving researchers affiliated with FlexiOss's own R&D program. Refer to the Instructions for Use and full regulatory documentation for complete safety information. This page is intended for healthcare professionals and does not constitute a clinical recommendation for any individual patient.";

const SourcePill = ({ label, href, testid, dark }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
      dark
        ? 'border-blue-400/40 bg-blue-500/10 text-blue-300 hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-200'
        : 'border-blue-500/40 bg-blue-50 text-blue-700 hover:border-blue-500 hover:bg-blue-100'
    }`}
  >
    <ExternalLink className="h-3 w-3" strokeWidth={2.4} />
    {label}
  </a>
);

const FaqItem = ({ item, isOpen, onToggle, dark }) => (
  <div
    data-testid={`faq-item-${item.id}`}
    className={`relative border-b ${dark ? 'border-slate-800' : 'border-slate-200'}`}
  >
    {/* Accent spine — fills when the row is open */}
    <span
      aria-hidden="true"
      className={`absolute left-0 top-0 bottom-0 w-[2px] origin-top transition-all duration-300 ${isOpen ? 'scale-y-100 bg-blue-500' : 'scale-y-0 bg-blue-500'}`}
    />
    <button
      type="button"
      onClick={onToggle}
      data-testid={`faq-question-${item.id}`}
      aria-expanded={isOpen}
      className="group flex w-full items-center gap-5 py-7 pl-5 pr-2 text-left sm:gap-6 sm:pl-8"
    >
      <span className="font-mono text-sm font-bold tabular-nums text-blue-500">{String(item.id).padStart(2, '0')}</span>
      <span
        className={`flex-1 text-lg font-bold leading-snug tracking-tight transition-colors duration-200 sm:text-xl ${
          isOpen ? 'text-blue-500' : dark ? 'text-white' : 'text-slate-900'
        } group-hover:text-blue-500`}
      >
        “{item.q}”
      </span>
      <ChevronDown
        className={`h-5 w-5 flex-shrink-0 text-blue-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        strokeWidth={2.4}
      />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div className="pb-9 pl-5 pr-2 sm:pl-[3.6rem] sm:pr-6" data-testid={`faq-answer-${item.id}`}>
            <p className={`text-[15px] leading-relaxed sm:text-base ${dark ? 'text-slate-300' : 'text-slate-700'}`}>{item.a}</p>

            {item.limitation && (
              <div
                data-testid={`faq-limitation-${item.id}`}
                className={`mt-6 border-l-[3px] border-amber-500 py-3 pl-5 pr-4 ${dark ? 'bg-amber-500/10' : 'bg-amber-50'}`}
              >
                <p className={`mb-1.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] ${dark ? 'text-amber-400' : 'text-amber-700'}`}>
                  <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.4} />
                  Limitation
                </p>
                <p className={`text-sm leading-relaxed ${dark ? 'text-amber-100/90' : 'text-amber-900'}`}>{item.limitation}</p>
              </div>
            )}

            {item.sources && (
              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  {item.sources.length > 1 ? 'Sources' : 'Source'}
                </span>
                {item.sources.map((s, i) => (
                  <SourcePill key={s.href} label={s.label} href={s.href} dark={dark} testid={`faq-source-${item.id}-${i + 1}`} />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FaqPage = () => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [openId, setOpenId] = useState(1);
  const accordionRef = useRef(null);

  const scrollToFaq = () => {
    accordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${SITE_URL}/faq#faq`,
    mainEntity: FAQ_ITEMS.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: it.limitation ? `${it.a} Limitation: ${it.limitation}` : it.a,
      },
    })),
  };

  return (
    <SubpageLayout productName="Clinical FAQ">
      <StructuredData id="faq-page" schema={faqSchema} />

      {/* Hero / Hook */}
      <section className="relative overflow-hidden bg-slate-950 pb-10 pt-14 sm:pt-20">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-1.5">
            <Microscope className="h-3.5 w-3.5 text-blue-500" strokeWidth={2.2} />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-500">For Healthcare Professionals · Evidence Library</span>
          </div>

          <button
            type="button"
            onClick={scrollToFaq}
            data-testid="faq-hook-card"
            className={`group relative block w-full overflow-hidden rounded-3xl border p-8 text-left transition-all duration-300 hover:border-blue-400/60 sm:p-12 ${dark ? 'border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950' : 'border-slate-200 bg-gradient-to-br from-white to-slate-50 shadow-sm'}`}
          >
            <span className="pointer-events-none absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-blue-500 via-blue-400/50 to-transparent" />
            <h1 className={`text-3xl font-black leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl ${dark ? 'text-white' : 'text-slate-900'}`}>
              Still skeptical about hydroxyapatite?{' '}
              <span className="text-blue-500">Here's the data.</span>
            </h1>
            <p className={`mt-5 max-w-2xl text-base leading-relaxed sm:text-lg ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
              What was actually failing in conventional HAP grafts — and what's structurally different in FlexiOss.
            </p>
            <span
              data-testid="faq-hook-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-bold text-white transition-all group-hover:bg-blue-500 group-hover:shadow-[0_14px_36px_-10px_rgba(37,99,235,0.7)]"
            >
              See the evidence
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" strokeWidth={2.4} />
            </span>
          </button>
          <div className="mt-6"><ReadAloud dark={dark} /></div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-slate-950 py-8">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className={`text-2xl font-black tracking-tight sm:text-3xl ${dark ? 'text-white' : 'text-slate-900'}`}>
            Hydroxyapatite concerns, answered with the published research
          </h2>
          <p className={`mt-5 text-sm leading-relaxed sm:text-base ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
            If you've worked with hydroxyapatite-based bone substitutes before and walked away frustrated, you're not
            alone — and you're not wrong about what you saw. Brittleness, poor resorption, particle migration, and
            limited handling characteristics are well-documented limitations of conventional ceramic HAP. FlexiOss® was
            engineered specifically around these failure points by embedding HAP granules in a curdlan (β-1,3-glucan)
            polymer matrix rather than delivering ceramic alone. Below is what the peer-reviewed evidence actually shows
            for each concern — including where that evidence is still early-stage.
          </p>
        </div>
      </section>

      {/* Accordion */}
      <section ref={accordionRef} id="faq-accordion" data-testid="faq-accordion" className="scroll-mt-24 bg-slate-950 pb-16">
        <div className={`mx-auto max-w-4xl border-t px-6 lg:px-8 ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
          {FAQ_ITEMS.map((item) => (
            <FaqItem
              key={item.id}
              item={item}
              dark={dark}
              isOpen={openId === item.id}
              onToggle={() => setOpenId(openId === item.id ? null : item.id)}
            />
          ))}
        </div>
      </section>

      {/* Research Library */}
      <section data-testid="faq-research-library" className={`border-t py-14 ${dark ? 'border-slate-800/60 bg-slate-900/40' : 'border-slate-200 bg-slate-100'}`}>
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="mb-2 flex items-center gap-2.5">
            <BookOpen className="h-5 w-5 text-blue-500" strokeWidth={2.2} />
            <h2 className={`text-2xl font-black tracking-tight sm:text-3xl ${dark ? 'text-white' : 'text-slate-900'}`}>Browse the published research</h2>
          </div>
          <p className={`mb-7 text-sm leading-relaxed sm:text-base ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
            All claims on this page are drawn directly from the sources below. We'd rather you read the primary
            literature than take our word for it.
          </p>
          <ul className={`divide-y overflow-hidden rounded-2xl border ${dark ? 'divide-slate-800 border-slate-800 bg-slate-900/60' : 'divide-slate-200 border-slate-200 bg-white'}`}>
            {RESEARCH_LIBRARY.map((r, i) => (
              <li key={r.href}>
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`research-link-${i + 1}`}
                  className={`group flex items-center justify-between gap-4 px-5 py-4 transition-colors sm:px-6 ${dark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50'}`}
                >
                  <span className={`text-sm font-medium sm:text-[15px] ${dark ? 'text-slate-200 group-hover:text-white' : 'text-slate-700 group-hover:text-slate-900'}`}>{r.label}</span>
                  <ExternalLink className="h-4 w-4 flex-shrink-0 text-slate-500 transition-colors group-hover:text-blue-500" strokeWidth={2.2} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Footer disclaimer — always visible, not collapsible */}
      <section className="bg-slate-950 py-12">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className={`flex items-start gap-3 rounded-xl border p-5 ${dark ? 'border-slate-800 bg-slate-900/40' : 'border-slate-200 bg-slate-100'}`}>
            <FlaskConical className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-500" strokeWidth={2} />
            <p data-testid="faq-disclaimer" className={`text-xs italic leading-relaxed sm:text-[13px] ${dark ? 'text-slate-500' : 'text-slate-600'}`}>
              {DISCLAIMER}
            </p>
          </div>
        </div>
      </section>
    </SubpageLayout>
  );
};

export default FaqPage;
